// Shopify's automatic discount sets the price; this code never sets a price.
export function giftState(cart, productId, threshold = 59900) {
  const gifts = cart.items.filter(item => Number(item.product_id) === Number(productId));
  const subtotal = cart.total_price - gifts.reduce((sum, item) => sum + item.final_line_price, 0);
  return { gifts, eligible: cart.currency === 'DKK' && subtotal >= threshold };
}

export function giftCorrections(cart, productId, threshold = 59900) {
  const { gifts, eligible } = giftState(cart, productId, threshold);
  const updates = {};
  let kept = false;
  for (const gift of gifts) {
    // Remove gifts that are charged, ineligible, or duplicated.
    if (!eligible || gift.final_line_price !== 0 || kept) updates[gift.key] = 0;
    else {
      kept = true;
      if (gift.quantity !== 1) updates[gift.key] = 1;
    }
  }
  return updates;
}

if (typeof customElements !== 'undefined' && !customElements.get('vantho-cart-gift')) {
  let queue = Promise.resolve();
  let checkingOut = false;
  let selected;
  const storageKey = 'vantho-gift-selected';
  try {
    const saved = sessionStorage.getItem(storageKey);
    if (saved !== null) selected = saved === 'true';
  } catch { /* In-memory selection still works when storage is unavailable. */ }
  function select(value) {
    selected = value;
    try { sessionStorage.setItem(storageKey, String(value)); } catch { /* optional persistence */ }
  }
  const enqueue = task => { queue = queue.then(task, task); return queue; };
  const root = () => window.Shopify?.routes?.root || '/';
  async function request(path, body) {
    const response = await fetch(root() + path, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await response.json();
    if (!response.ok) throw new Error('Gaven kunne ikke opdateres. Prøv igen.');
    return data;
  }
  async function refresh() {
    const sections = await request('?sections=cart-drawer,cart,cart-bubble');
    if (!sections['cart-drawer']) throw new Error('Kurven kunne ikke opdateres. Genindlæs siden.');
    document.dispatchEvent(new CustomEvent('cart-update', { detail: { sections } }));
  }
  async function removeGifts(gifts) {
    if (gifts.length) await request('cart/update.js', {
      updates: Object.fromEntries(gifts.map(item => [item.key, 0])),
    });
  }
  customElements.define('vantho-cart-gift', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (selected === undefined) select(this.input.checked);
      this.input.checked = selected;
      this.input.onchange = () => {
        select(this.input.checked);
        this.querySelector('.cart_gift_error').hidden = true;
      };
      enqueue(() => this.restoreSelection()).catch(() => this.showError());
    }
    async restoreSelection() {
      if (!this.isConnected || checkingOut) return;
      const cart = await request('cart.js');
      const { gifts, eligible } = giftState(cart, this.dataset.productId, Number(this.dataset.threshold));
      if (checkingOut) return;
      if (!eligible) select(false);
      this.input.checked = selected;
      // Also migrate gifts already added by the previous implementation or checkout.
      if (gifts.length) {
        await removeGifts(gifts);
        await refresh();
      }
    }
    showError(message = 'Gaven kunne ikke opdateres. Genindlæs kurven og prøv igen.') {
      const target = document.querySelector('vantho-cart-gift') || this;
      const error = target.querySelector('.cart_gift_error');
      error.textContent = message;
      error.hidden = false;
    }
    async prepareCheckout() {
      const productId = this.dataset.productId;
      const threshold = Number(this.dataset.threshold);
      let cart = await request('cart.js');
      let state = giftState(cart, productId, threshold);
      await removeGifts(state.gifts);
      if (selected && state.eligible) {
        if (!this.dataset.variantId) throw new Error('Gaven er ikke tilgængelig. Fjern markeringen for at fortsætte.');
        await request('cart/add.js', { items: [{ id: Number(this.dataset.variantId), quantity: 1, properties: { _vantho_gift: '599' } }] });
        cart = await request('cart.js');
        state = giftState(cart, productId, threshold);
        if (!state.eligible || state.gifts.length !== 1 || state.gifts[0].quantity !== 1 || state.gifts[0].final_line_price !== 0) {
          await removeGifts(state.gifts);
          throw new Error('Nettet kunne ikke tilføjes gratis. Prøv igen, eller fjern markeringen for at fortsætte uden gaven.');
        }
      } else if (!state.eligible) {
        select(false);
      }
    }
  });

  async function checkout(event, destination) {
    const gift = document.querySelector('vantho-cart-gift');
    if (!gift) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingOut) return;
    checkingOut = true;
    const button = document.querySelector('.cart_checkout_btn');
    button?.setAttribute('aria-busy', 'true');
    gift.input.disabled = true;
    try {
      await enqueue(() => gift.prepareCheckout());
      window.location.assign(destination || root() + 'checkout');
    } catch (error) {
      checkingOut = false;
      gift.input.disabled = false;
      button?.removeAttribute('aria-busy');
      gift.showError(error.message);
    }
  }
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a.cart_checkout_btn, a[href="/checkout"], a[href$="/checkout"]');
    if (link) checkout(event, link.href);
  }, true);
  document.addEventListener('submit', event => {
    if (event.submitter?.name === 'checkout') checkout(event);
  }, true);
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    checkingOut = false;
    const gift = document.querySelector('vantho-cart-gift');
    if (gift) {
      gift.input.disabled = false;
      enqueue(() => gift.restoreSelection()).catch(() => gift.showError());
    }
  });
}

